// src/pages/PftMoveFlow.js
//
// "Move to Lung Center Referrals" for one PFT Uploads card:
//   1. look for the patient in the system (exact MRN, or name + date of birth);
//   2. no match -> confirm and create the patient (optional diagnosis);
//      match    -> the person picks the patient, then sees every field side by side:
//                  empty fields fill, differing ones are the person's pick (default:
//                  keep what's there), new PFT dates are added. Status never changes.
// A person can always say "none of these is this patient" and create a new one.
// Items not yet ticked don't block a move (decided 2026-10-06): the person is warned,
// confirms, and the number unticked is recorded in the audit trail.

import { useState } from "react";
import { ArrowRightCircle, Loader2, UserPlus, GitMerge, AlertTriangle, X } from "lucide-react";
import { findCardMatches, patientHref } from "../services/pftMovePlan";
import PftMatchCard from "./PftMatchCard";
import PftMergeRows, { pdfsOfCard, withChoice } from "./PftMergeRows";
import {
  createPatientFromCard,
  loadMatchCandidates,
  loadMergePlan,
  MatchFoundError,
  mergeIntoFirestorePatient,
  PatientChangedError,
} from "../services/pftMoveService";

const btn =
  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40";
const primary =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/40 to-blue-500/40 border border-purple-300/30 hover:from-purple-500/60 hover:to-blue-500/60 text-sm font-medium text-white transition disabled:opacity-40";

export default function PftMoveFlow({ card, blockedReason, uncheckedCount = 0, onDone }) {
  const [step, setStep] = useState("idle"); // idle | checking | working | matches | merge | error
  const [matches, setMatches] = useState([]);
  const [target, setTarget] = useState(null);
  const [merge, setMerge] = useState(null); // { plan, signature }
  const [choices, setChoices] = useState({ profile: {}, tests: {} });
  const [error, setError] = useState(null);

  const start = async () => {
    if (
      uncheckedCount > 0 &&
      !window.confirm(
        `${uncheckedCount} item(s) haven't been checked against the PDF. Move this patient anyway?`,
      )
    )
      return;
    setError(null);
    setStep("checking");
    try {
      const found = findCardMatches(card, await loadMatchCandidates());
      setMatches(found);
      // No possible match: create the patient straight away (decided 2026-10-06; staff
      // don't confirm each one). The diagnosis is set later on the patient page.
      if (found.length) setStep("matches");
      else await create(false);
    } catch (e) {
      setError(e.message);
      setStep("error");
    }
  };

  // differentPerson: the person looked at the possible matches and chose "none of these".
  const create = async (differentPerson) => {
    setStep("working");
    setError(null);
    try {
      await createPatientFromCard(card, { diagnosis: [], allowMatches: differentPerson, uncheckedItems: uncheckedCount });
      onDone(`${card.name || "Patient"} added to Lung Center Referrals.`);
    } catch (e) {
      if (e instanceof MatchFoundError && !differentPerson) {
        setMatches(e.matches);
        setStep("matches");
        setError("A possible match appeared while checking. Please look at it.");
      } else {
        setError(e.message);
        setStep(differentPerson ? "matches" : "error");
      }
    }
  };

  const pick = async (m) => {
    setError(null);
    setTarget(m);
    setStep("checking");
    try {
      const { plan, signature } = await loadMergePlan(card, m);
      setMerge({ plan, signature });
      setChoices({ profile: {}, tests: {} });
      setStep("merge");
    } catch (e) {
      setError(e.message);
      setStep("matches");
    }
  };

  const doMerge = async () => {
    setStep("working");
    setError(null);
    try {
      const r = await mergeIntoFirestorePatient(card, target, choices, merge.signature, { uncheckedItems: uncheckedCount });
      onDone(
        `Merged into ${target.record.name || target.record.fullName || "the patient"}: ${r.addedTests} new PFT date(s) added` +
          `${r.updatedTests ? `, ${r.updatedTests} filled in` : ""}.`,
      );
    } catch (e) {
      if (e instanceof PatientChangedError) {
        setError(e.message);
        await pick(target);
      } else {
        setError(e.message);
        setStep("merge");
      }
    }
  };

  const setChoice = (scope, key, field, value) => setChoices((c) => withChoice(c, scope, key, field, value));

  if (blockedReason) {
    return (
      <button disabled title={blockedReason} className={`ml-auto ${primary}`}>
        <ArrowRightCircle className="w-4 h-4" />
        Move to Lung Center Referrals
      </button>
    );
  }

  const TITLES = {
    checking: "Checking for this patient",
    working: "Saving",
    error: "Couldn't move this patient",
    matches: "This patient may already be in the system",
    merge: "Merge into an existing patient",
  };
  const warn = step === "matches";
  const busy = step === "checking" || step === "working";

  return (
    <>
      <div className="flex ml-auto">
        <button onClick={start} disabled={step !== "idle"} className={primary}>
          <ArrowRightCircle className="w-4 h-4" />
          Move to Lung Center Referrals
        </button>
      </div>
      {step !== "idle" && (
      <div
        className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={TITLES[step]}
      >
      <div
        className={`relative w-full max-w-2xl rounded-2xl border ${
          warn ? "border-amber-400/40" : "border-white/10"
        } pft-modal-panel bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.85)] text-slate-100 p-5 space-y-4`}
      >
      <div className="flex items-center gap-2">
        {warn ? <AlertTriangle className="w-5 h-5 text-amber-300" /> : step === "merge" ? <GitMerge className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
        <h3 className="text-base font-semibold">{TITLES[step]}</h3>
        <button
          onClick={() => setStep("idle")}
          disabled={busy}
          aria-label="Close"
          className={`ml-auto ${btn}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {(step === "checking" || step === "working") && (
        <p className="text-[13px] text-slate-200 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          {step === "checking" ? "Checking whether this patient is already in the system…" : "Saving…"}
        </p>
      )}

      {step === "matches" && (
        <div className="space-y-3">
          <p className="text-[13px] text-amber-100">
            We found {matches.length} existing record{matches.length === 1 ? "" : "s"} that may be{" "}
            <span className="font-semibold">{card.name || "this patient"}</span>. Pick the right one to merge the PFT data into it,
            or create a new patient if none of them is this person.
          </p>
          {matches.map((m) => {
            const r = m.record;
            return (
              <div key={`${m.store}-${r.id}`} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 flex flex-wrap items-center gap-2">
                <PftMatchCard match={m} />
                <button onClick={() => pick(m)} className={`ml-auto ${btn}`}>
                  <GitMerge className="w-3.5 h-3.5" />
                  Merge into this patient
                </button>
              </div>
            );
          })}
          <div className="flex flex-wrap gap-2">
            <button onClick={() => create(true)} className={btn}>None of these: create a new patient</button>
            <button onClick={() => setStep("idle")} className={btn}>Cancel</button>
          </div>
        </div>
      )}

      {step === "merge" && merge && (
        <div className="space-y-3">
          <p className="text-[13px] text-slate-100">
            Merging into <span className="font-semibold">{target.record.name || target.record.fullName}</span>. Their status stays{" "}
            <span className="font-semibold">{target.record.status || "as it is"}</span>. Nothing already there changes unless you pick the PFT value.
          </p>
          <PftMergeRows
            plan={merge.plan}
            choices={choices}
            setChoice={setChoice}
            regxHref={patientHref(target)}
            pdfs={pdfsOfCard(card)}
          />
          <div className="flex gap-2">
            <button onClick={doMerge} className={primary}>
              <GitMerge className="w-4 h-4" />
              Merge
            </button>
            <button onClick={() => setStep("matches")} className={btn}>Back</button>
          </div>
        </div>
      )}

      {error && <p className="text-[12px] text-amber-200">{error}</p>}
      </div>
      </div>
      )}
    </>
  );
}
